import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Car, Gauge, MapPin, Wrench } from 'lucide-react';
import { BRANDS, type Brand } from '../data/brands';
import { BrandLogo } from './BrandLogo';
export function BrandSection() {
  return <>
      {/* Moder trak — samostojna sekcija */}
      <section aria-labelledby="brands-strip-title" className="w-full bg-aw-navy" data-id="element-23" data-mp="2de2mqsrp8hpd">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-7 lg:px-10 lg:py-9" data-id="element-24" data-mp="3ayyl7w734q7g">
          <h2 id="brands-strip-title" className="text-center text-base text-white/90 sm:text-lg" data-id="element-25" data-mp="360x43gt7z0ym">
            Za vas zastopamo
          </h2>
          <ul className="mx-auto mt-5 flex max-w-[660px] flex-wrap items-center justify-center gap-x-10 gap-y-7 sm:mt-6 sm:gap-x-14" data-id="element-26" data-mp="318doyfevvxrp">
            {BRANDS.map(brand => <li key={brand.id} className="opacity-85" data-id="element-27" data-mp="3trj9f0bua74x">
                <BrandLogo brand={brand} color="white" size={28} data-id="element-28" data-mp="3p0az822oqhqp" />
              </li>)}
          </ul>
        </div>
      </section>

      {/* Kartice znamk */}
      <section aria-labelledby="brands-grid-title" className="w-full bg-slate-50" data-id="element-29" data-mp="25dde0qfek4gr">
        <div className="mx-auto w-full max-w-[1360px] py-10 sm:py-16" data-id="element-30" data-mp="29c2p2ccyx9x8">
          <div className="flex flex-wrap items-end justify-between gap-4 px-5 lg:px-10" data-id="element-31" data-mp="1vkeguk5ub5lj">
            <div data-id="element-32" data-mp="20jqzsttlbsiu">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-aw-accent sm:text-xs" data-id="element-33" data-mp="20j95i2qhnodk">
                Naša ponudba
              </p>
              <h2 id="brands-grid-title" className="mt-2 font-heading text-xl font-bold text-aw-navy sm:text-3xl" data-id="element-34" data-mp="1qm4iy1sqdqw8">
                Izberite blagovno znamko
              </h2>
              <p className="mt-2 max-w-xl text-sm text-slate-500" data-id="element-35" data-mp="1hkwm7o5v4y49">
                Zaloga vozil, testna vožnja in servis — vse na enem mestu, za vsako znamko posebej.
              </p>
            </div>
            <a href="#" className="group flex items-center gap-2 text-sm font-semibold text-aw-navy hover:text-aw-accent" data-id="element-36" data-mp="3bzz7tarsjw1w">
              Vsa vozila na zalogi
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-37" data-mp="35j7dlgsne97s" />
            </a>
          </div>

          {/* Mobilni prikaz — vrtiljak z delno vidno naslednjo kartico */}
          <div className="sm:hidden" data-id="element-38" data-mp="2vmb3j6n3pf14">
            <BrandCarousel data-id="element-39" data-mp="2vndwdk3zhrjo" />
          </div>

          {/* Namizni prikaz — mreža 4 + 3 */}
          <ul className="mt-10 hidden auto-rows-fr gap-5 px-5 sm:grid sm:grid-cols-2 lg:grid-cols-4 lg:px-10" data-id="element-40" data-mp="3t88fel6z6m43">
            {BRANDS.map(brand => <li key={brand.id} data-id="element-41" data-mp="3jip0fqmdvcei">
                <BrandCard brand={brand} data-id="element-42" data-mp="3jhnn1fhwadtm" />
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}
function BrandCarousel() {
  const trackRef = useRef<HTMLUListElement | null>(null);
  const [active, setActive] = useState(0);
  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const first = track.firstElementChild as HTMLElement | null;
    if (!first) return;
    const step = first.getBoundingClientRect().width + 16;
    setActive(Math.min(BRANDS.length - 1, Math.max(0, Math.round(track.scrollLeft / step))));
  }, []);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const first = track.firstElementChild as HTMLElement | null;
    if (!first) return;
    const step = first.getBoundingClientRect().width + 16;
    track.scrollTo({
      left: index * step,
      behavior: 'smooth'
    });
  };
  return <div className="mt-7" data-id="element-43" data-mp="16afkfgggckxl">
      <ul ref={trackRef} onScroll={onScroll} aria-label="Blagovne znamke" className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-44" data-mp="1b3iio1w6cik4">
        {BRANDS.map(brand => <li key={brand.id} className="w-[82%] shrink-0 snap-start last:mr-5" data-id="element-45" data-mp="1p28ele79cc6w">
            <BrandCard brand={brand} data-id="element-46" data-mp="2mrmq5lanrauw" />
          </li>)}
      </ul>

      <div className="mt-4 flex items-center justify-center gap-2 px-5" data-id="element-47" data-mp="2mps3a1ftt8o6">
        {BRANDS.map((brand, i) => <button key={brand.id} type="button" onClick={() => goTo(i)} aria-label={`Pokaži ${brand.name}`} aria-current={i === active} className={`h-1.5 transition-all ${i === active ? 'w-6 bg-aw-navy' : 'w-1.5 bg-slate-300'}`} data-id="element-48" data-mp="2vk6tv05t45ml" data-mpi={i} />)}
      </div>
    </div>;
}
function BrandCard({
  brand
}: {
  brand: Brand;
}) {
  return <article className="flex h-full flex-col border border-slate-200 bg-white p-6 transition-shadow duration-300 hover:shadow-lg" data-id="element-49" data-mp="2ctysonlo3p91">
      <div className="flex h-12 items-start" data-id="element-50" data-mp="27tat3djdi037">
        <BrandLogo brand={brand} color="navy" size={40} data-id="element-51" data-mp="3o8cfdp4opynh" />
      </div>

      <h3 className="mt-5 font-heading text-xl font-bold text-aw-navy" data-id="element-52" data-mp="3oaouzvmyzwxd">
        <Link to={`/${brand.id}`} className="hover:text-aw-accent" data-id="element-53" data-mp="3j997lwxblebt">
          {brand.name}
        </Link>
      </h3>
      <p className="mt-1 text-sm text-slate-500" data-id="element-54" data-mp="3rsw20z69j3g8">{brand.tagline}</p>

      <ul className="mt-4 flex flex-wrap gap-1.5" data-id="element-55" data-mp="3vt68ecxyvvvm">
        {brand.locations.map(loc => <li key={loc} className="flex items-center gap-1 bg-slate-50 px-2 py-1 text-[11px] text-slate-500" data-id="element-56" data-mp="3htnbyheiq8z5">
            <MapPin className="h-3 w-3" aria-hidden="true" data-id="element-57" data-mp="3mtjfkfjazye6" />
            {loc}
          </li>)}
      </ul>

      <ul className="mt-auto border-t border-slate-100 pt-3" data-id="element-58" data-mp="jazupy2yfute">
        <CardAction label={`Zaloga vozil ${brand.name}`} Icon={Car} to={`/${brand.id}`} data-id="element-59" data-mp="ja6n6uw691a7" />
        <CardAction label="Prijava na testno vožnjo" Icon={Gauge} data-id="element-60" data-mp="4lk88uagibji" />
        {brand.hasService && <CardAction label="Servis in cenitev" Icon={Wrench} data-id="element-61" data-mp="9jvi350r4m1q" />}
      </ul>
    </article>;
}
function CardAction({
  label,
  Icon,
  to
}: {
  label: string;
  Icon: typeof Car;
  to?: string;
}) {
  const className = 'group flex items-center gap-2.5 py-2.5 text-sm text-slate-600 transition-colors hover:text-aw-navy';
  const content = <>
      <Icon className="h-4 w-4 shrink-0 text-aw-navy" aria-hidden="true" data-id="element-62" data-mp="16yd88umlo8co" />
      <span className="font-medium" data-id="element-63" data-mp="16wa9nm7dt6wc">{label}</span>
      <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-aw-accent" data-id="element-64" data-mp="11yikv121a0wd" />
    </>;
  return <li data-id="element-65" data-mp="sylp6fh881rt">
      {to ? <Link to={to} className={className} data-id="element-66" data-mp="sz3wc75fg0ei">
          {content}
        </Link> : <a href="#" className={className} data-id="element-67" data-mp="o7dfdibziee4">
          {content}
        </a>}
    </li>;
}